import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'

import xor from 'lodash/xor'

import Button from '@eig-builder/core-ui/Button'
import TextField from '@eig-builder/core-ui/TextField'
import Checkbox from '@eig-builder/core-ui/Checkbox'
import FormControlLabel from '@eig-builder/core-ui/FormControlLabel'

import { DataElementLocations } from '@eig-builder/core-utils/helpers/tagging-helper'

import ButtonWithLoader from 'core/components/button-with-loader'
import Modal from 'core/components/modal'

import RefundInformation from '../../refund'
import RegisteredDomains from './registered-domains'
import { DIRECTLY } from '../../services-tab/constants'

import { RefundContext } from '../../refund/context'
import { DowngradeAccountContext } from './context'

import Text from '@eig-builder/module-localization'

const filterRefundableServices = refundableServices => {
  return (
    refundableServices &&
    refundableServices.map(item => {
      return {
        planName: item.sku,
        renewDate: item.renew_date,
        proRatedAmount: item.prorated_amount,
        billingServiceId: item.billing_service_id,
        term: item.term,
        refundOrCreditAmount: item.refund_or_credit_amount,
        initialDomainRegistrationFee: item.initial_domain_registration_fee,
        serviceId: item.id
      }
    })
  )
}

const DowngradeAccountDialog = props => {
  const { id: accountId, partnerId } = props.account
  const { downgradeSuccessLogs, downgradePending } = props
  const { domainsToKeep } = useContext(DowngradeAccountContext)
  const { refundType } = useContext(RefundContext)

  const posting = useSelector(state => state.crm.postRefundServicesRetrieving)
  const postingSuccess = useSelector(state => state.crm.postRefundServicesSuccess)

  const refundableServices = useSelector(
    state => state.crm.getRefundableServicesResponse && state.crm.getRefundableServicesResponse.refundable_services
  )

  const refundServices = filterRefundableServices(refundableServices)

  const [reason, setReason] = useState('')
  const [checkboxChecked, setCheckboxChecked] = useState(true)
  const [skipDomainRegistrationFreePlanIds, setIds] = useState([])

  useEffect(() => {
    props.getRegisteredDomains(accountId, partnerId)
  }, [])

  const handleSubmit = () => {
    // Refund services can not be an empty list or be refunded without a pro rated amount
    const refundList =
      refundServices &&
      refundServices.filter(item => item.refundOrCreditAmount > 0).map(i => parseInt(i.billingServiceId))

    const body = {
      keep: domainsToKeep,
      remove: props.registeredDomains.filter(d => !domainsToKeep.includes(d.id)).map(d => d.id),
      reason,
      email: checkboxChecked,
      refund: refundList.length > 0 && refundType !== '-1',
      skip_initial_domain_registration_fee_plan_ids: skipDomainRegistrationFreePlanIds
    }

    props.downgradeAccount(body, accountId, partnerId)
  }

  const handleClose = () => {
    props.loadAccountById(partnerId, accountId)
    props.handleDialog()
  }

  const handleCheckbox = () => {
    setCheckboxChecked(!checkboxChecked)
  }

  const downgradeLogs = downgradeSuccessLogs && downgradeSuccessLogs.trim().split('\n')

  const actions = downgradeLogs
    ? [
      <Button
        key='downgrade-account-close'
        onClick={handleClose}
        dataElementLocation={DataElementLocations.BODY}
        dataElementLabel='cst-account-downgrade--close'
        dataElementId='cst-account-downgrade--close'
      >
        <Text message='modules.crm.components.account.close' />
      </Button>
    ]
    : [
      <Button
        key='downgrade-account-cancel'
        color='secondary'
        disabled={downgradePending || posting}
        onClick={props.handleDialog}
        dataElementLocation={DataElementLocations.BODY}
        dataElementLabel='cst-account-downgrade-cancel'
        dataElementId='cst-account-downgrade-cancel'
      >
        <Text message='modules.crm.components.account.cancel' />
      </Button>,
      <ButtonWithLoader
        key='downgrade-account-submit'
        disabled={props.registeredDomainsRetrieving || downgradePending || posting}
        onClick={handleSubmit}
        dataElementSuffix='account-downgrade--submit'
        showLoader={downgradePending || posting}
        text={<Text message='modules.crm.components.account.submit' />}
      />
    ]

  return (
    <Modal
      actions={actions}
      dataElementPrefix='cst-account--downgrade'
      disableBackdropClick
      handleClose={props.handleDialog}
      open
      title={<Text message='modules.crm.components.account.downgradeAccount.title' />}
    >
      {downgradeLogs && !posting ? (
        <div>
          {downgradeLogs.map(line => (
            <div key={line}>{line}</div>
          ))}
          {postingSuccess ? <Text message='modules.crm.components.account.downgradeAccount.refundServices' /> : ''}
        </div>
      ) : (
        <>
          <RegisteredDomains
            retrieving={props.registeredDomainsRetrieving}
            registeredDomains={props.registeredDomains}
            />
          <div className='mt-3'>
            <RefundInformation
              refundServices={refundServices}
              handleSkipId={serviceId => {
                  setIds(xor(skipDomainRegistrationFreePlanIds, [serviceId]))
                }}
              skipDomainRegistrationFreePlanIds={skipDomainRegistrationFreePlanIds}
              cancelType={DIRECTLY}
              />
          </div>
          <TextField
            fullWidth
            className='mt-4'
            multiline
            rows={3}
            variant='outlined'
            label={<Text message='modules.crm.components.account.downgradeReason' />}
            value={reason}
            onChange={e => setReason(e.target.value)}
            dataElementLocation={DataElementLocations.BODY}
            dataElementLabel='cst-account-downgrade--reason'
            dataElementId='cst-account-downgrade--reason'
            />
          <FormControlLabel
            className='mt-2'
            control={
              <Checkbox
                checked={checkboxChecked}
                onChange={handleCheckbox}
                value='sendEmailDowngrade'
                color='primary'
                dataElementLocation={DataElementLocations.BODY}
                dataElementLabel='cst-account-downgrade--send-email-checkbox'
                dataElementId='cst-account-downgrade--send-email-checkbox'
                />
              }
            label={<Text message='modules.crm.components.account.sendEmailDowngrade' />}
            />
        </>
        )}
    </Modal>
  )
}

DowngradeAccountDialog.propTypes = {
  account: PropTypes.object.isRequired,
  getRegisteredDomains: PropTypes.func.isRequired,
  downgradeAccount: PropTypes.func.isRequired,
  loadAccountById: PropTypes.func.isRequired,
  handleDialog: PropTypes.func.isRequired,
  registeredDomainsRetrieving: PropTypes.bool.isRequired,
  registeredDomains: PropTypes.array,
  downgradePending: PropTypes.bool.isRequired,
  downgradeSuccessLogs: PropTypes.string
}

export default DowngradeAccountDialog
